import React, { useEffect, useMemo, useState } from 'react';
import SuccessBurst from './SuccessBurst';
import { playRewardSound } from '../../lib/sfx';

function RewardModal({
  language,
  isOpen,
  onClose,
  nickname,
  lessonTitle,
  pointsAwarded,
  multiplier,
  isFresh,
  rankLabel,
  level,
  totalPoints,
  nextPointsToNext,
  progressPct
}) {
  const [burst, setBurst] = useState(0);

  useEffect(() => {
    if (!isOpen) return;
    // Only play the big reward sound for fresh awards.
    if (isFresh) playRewardSound();
    setBurst((x) => x + 1);
  }, [isOpen, isFresh]);

  const title = useMemo(() => {
    if (language === 'ro') return 'Lecție completată';
    return 'Lesson completed';
  }, [language]);

  const headline = useMemo(() => {
    const name = String(nickname || '').trim() || (language === 'ro' ? 'Campion' : 'Champion');
    const pts = Number(pointsAwarded || 0);
    const mult = Number(multiplier || 1);

    if (language === 'ro') {
      if (!isFresh) return `Felicitări, ${name}! Lecție completată.`;
      const streak = mult >= 2 ? ' Streak! Ai dublat recompensa.' : '';
      return `Bravo, ${name}! Ai câștigat încă ${pts} puncte IQ.${streak}`;
    }
    if (!isFresh) return `Nice work, ${name}! Lesson completed.`;
    const streak = mult >= 2 ? ' Streak! You doubled your reward.' : '';
    return `Great job, ${name}! You earned +${pts} IQ Points.${streak}`;
  }, [language, nickname, pointsAwarded, multiplier, isFresh]);

  const sub = useMemo(() => {
    const lt = String(lessonTitle || '').trim();
    if (language === 'ro') {
      if (!isFresh) {
        return lt
          ? `Ai terminat: “${lt}”. Recompensa pentru această lecție a fost deja colectată, dar progresul rămâne al tău.`
          : 'Recompensa pentru această lecție a fost deja colectată, dar progresul rămâne al tău.';
      }
      return lt
        ? `Ai terminat: “${lt}”. Ești pe drumul cel bun pe Calea Maestrului.`
        : 'Ești pe drumul cel bun pe Calea Maestrului.';
    }
    if (!isFresh) {
      return lt
        ? `You finished: “${lt}”. This lesson’s reward was already claimed, but your progress is still yours.`
        : 'This lesson’s reward was already claimed, but your progress is still yours.';
    }
    return lt
      ? `You finished: “${lt}”. You’re on the right track on The Master’s Path.`
      : 'You’re on the right track on The Master’s Path.';
  }, [language, lessonTitle, isFresh]);

  if (!isOpen) return null;

  return (
    <div className="cm-reward-backdrop" role="dialog" aria-label={title}>
      <div className="cm-reward-card">
        <button className="cm-reward-close" onClick={onClose} aria-label="Close">
          ×
        </button>

        <div className="cm-reward-top">
          <div className="cm-reward-medal" aria-hidden="true">
            <div className="cm-reward-ribbon r1" />
            <div className="cm-reward-ribbon r2" />
            <div className="cm-reward-coin">
              <div className="cm-reward-coin-inner">
                <div className="cm-reward-coin-star">★</div>
              </div>
              <div className="cm-reward-shine" />
            </div>
            <SuccessBurst trigger={burst} />
          </div>
        </div>

        <div className="cm-reward-title">{headline}</div>
        <div className="cm-reward-sub">{sub}</div>

        <div className="cm-reward-stats">
          <div className="cm-reward-pill">
            <div className="k">{language === 'ro' ? 'Rang' : 'Rank'}</div>
            <div className="v">{rankLabel} {language === 'ro' ? 'Nivel' : 'Level'} {level}</div>
          </div>
          <div className="cm-reward-pill">
            <div className="k">{language === 'ro' ? 'Total' : 'Total'}</div>
            <div className="v">{totalPoints} {language === 'ro' ? 'puncte IQ' : 'IQ Points'}</div>
          </div>
        </div>

        <div className="cm-reward-progress">
          <div className="cm-reward-progress-head">
            <span className="pct">{progressPct}%</span>
            <span className="txt">
              {language === 'ro'
                ? `Încă ${nextPointsToNext} puncte IQ până la următorul nivel`
                : `${nextPointsToNext} IQ Points to the next level`}
            </span>
          </div>
          <div className="cm-reward-progress-bar">
            <div className="cm-reward-progress-fill" style={{ width: `${progressPct}%` }} />
          </div>
        </div>

        <div className="cm-reward-actions">
          <button className="cm-reward-primary" onClick={onClose}>
            {language === 'ro' ? 'Continuă' : 'Continue'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default RewardModal;
